import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import {
  Button,
  Image,
  Input,
  Popconfirm,
  Space,
  Typography,
  Upload,
} from 'antd'
import type { SectionAddendumAttachment } from 'pages/reporting/reports/rug/rugReportData'
import React from 'react'
import styled from 'styled-components'

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
`
const PreviewImage = styled(Image)`
  max-height: 12rem;
  max-width: 100%;
  object-fit: contain;
`
const FileDetails = styled.div`
  overflow-wrap: anywhere;
`

interface Props {
  attachment: SectionAddendumAttachment
  url: string | undefined
  disabled: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  onMove: (id: string, direction: -1 | 1) => void
  onRemove: (id: string) => void
  onCaption: (id: string, caption: string) => void
  onReplace: (id: string, attachment: SectionAddendumAttachment) => void
  uploadFile: (file: File) => Promise<SectionAddendumAttachment | undefined>
}

export function AddendumAttachmentRow({
  attachment,
  url,
  disabled,
  canMoveUp,
  canMoveDown,
  onMove,
  onRemove,
  onCaption,
  onReplace,
  uploadFile,
}: Props) {
  const replace = async (file: File) => {
    const replacement = await uploadFile(file)
    if (replacement) {
      onReplace(attachment.id, { ...replacement, caption: attachment.caption })
    }
    return Upload.LIST_IGNORE
  }
  const changeCaption = (event: React.ChangeEvent<HTMLInputElement>) =>
    onCaption(attachment.id, event.target.value)
  const remove = () => onRemove(attachment.id)
  const moveUp = () => onMove(attachment.id, -1)
  const moveDown = () => onMove(attachment.id, 1)

  return (
    <Row>
      <FileDetails>
        <Typography.Text strong>{attachment.fileName}</Typography.Text>
        {attachment.kind === 'pdf' && (
          <Typography.Text type='secondary'>
            {' '}
            · {attachment.pageCount} pages
          </Typography.Text>
        )}
      </FileDetails>
      {url && attachment.kind === 'image' && (
        <PreviewImage
          src={url}
          alt={attachment.caption || attachment.fileName}
        />
      )}
      {url && attachment.kind === 'pdf' && (
        <a href={url} target='_blank' rel='noopener noreferrer'>
          Preview PDF
        </a>
      )}
      <Input
        aria-label={`Caption for ${attachment.fileName}`}
        placeholder='Optional caption'
        maxLength={300}
        showCount
        value={attachment.caption ?? ''}
        onChange={changeCaption}
        disabled={disabled}
      />
      <Space wrap>
        <Button
          aria-label={`Move ${attachment.fileName} up`}
          icon={<ArrowUpOutlined />}
          disabled={disabled || !canMoveUp}
          onClick={moveUp}
        />
        <Button
          aria-label={`Move ${attachment.fileName} down`}
          icon={<ArrowDownOutlined />}
          disabled={disabled || !canMoveDown}
          onClick={moveDown}
        />
        <Upload
          accept='.png,.jpg,.jpeg,.pdf'
          showUploadList={false}
          beforeUpload={replace}
          disabled={disabled}>
          <Button disabled={disabled}>Replace</Button>
        </Upload>
        <Popconfirm
          title='Remove this attachment?'
          description='Save the addendum to apply this change.'
          onConfirm={remove}
          disabled={disabled}>
          <Button danger disabled={disabled} icon={<DeleteOutlined />}>
            Remove
          </Button>
        </Popconfirm>
      </Space>
    </Row>
  )
}
